/**
 * First-run onboarding (NEWS-297; the wizard itself is NEWS-78/128/146).
 *
 * Fourth seam out of `app.tsx`. Self-contained apart from the discovery dialog
 * the Topics step opens — the same one the sidebar opens, not a second one
 * (FR-24.18). That is the point of that step.
 *
 * There is no Source step any more (NM-12): AI-provider configuration is
 * environment-only, so there is nothing for a new user to authenticate here.
 * Whether a provider is usable is read from `state.source.available`, which the
 * Topics step uses to decide whether it can offer discovery.
 *
 * Kerf UI since NM-QJFW31: a `DialogSurface`, a native `<dialog>` (the frame
 * `appDialog` uses), whose title is the current step's question.
 * The choices are kerf UI `ChoiceChips`; the location is a `TextField`.
 *
 * Rendering only: `onboarding-next`, `onboarding-skip`, `open-discover` and the
 * starter chips are handled by `delegate()` in `app.tsx` (NEWS-126). Which step
 * is current, what is ticked and what gets created on Finish live in the store
 * and in `onboarding.ts` — this file only draws them.
 */

import { Button } from '@kerfjs/ui/button';
import { ChoiceChips } from '@kerfjs/ui/choice-chips';
import { deviceClass } from '@kerfjs/ui/device-class';
import { List } from '@kerfjs/ui/list';
import { Row } from '@kerfjs/ui/row';
import { Spacer } from '@kerfjs/ui/spacer';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { PROFILE_PAGE_COUNT, PROFILE_PAGES } from '../../../profiles.js';
import type { AppState, OnboardingStep } from '../../app/stores.js';
import { appStore, ONBOARDING_STEPS, STARTER_TOPICS } from '../../app/stores.js';
import { icon } from '../../components/icons.js';
import { LOCATION_QUICK_PICKS, newlyPickedProfiles, onboardingCountText, onboardingFinishLabel } from './onboarding.js';

/**
 * First-run flow (NEWS-78).
 *
 * Four steps, because a new user has four things to learn or decide and no
 * reason to guess at any of them: what the app does, who they are, where they
 * are, and what to watch. Skippable at every step — an onboarding you can't
 * escape is worse than none.
 */
/** Human labels for the step markers' accessible names (NM-246). */
const STEP_LABELS: Record<OnboardingStep, string> = {
  welcome: 'Welcome',
  profiles: 'Interests',
  location: 'Location',
  topics: 'Topics',
};

/** Each step's question — the dialog's title, so it is also its accessible name. */
const STEP_TITLES: Record<OnboardingStep, string> = {
  welcome: 'Newsmonger Watches Topics, Not Feeds',
  profiles: 'What Are You Into?',
  location: 'Where Are You?',
  topics: 'What Should Newsmonger Watch?',
};

/** The viewport's device class (kerf UI), read during render so the presentation follows it. */
const device = deviceClass();

/** Supporting prose under a step's lead. */
function note(text: string): SafeHtml {
  return (
    <Text tone="quiet" size="compact">
      {text}
    </Text>
  );
}

export function onboardingJsx(step: OnboardingStep): SafeHtml {
  const s = appStore.state.value;
  const index = ONBOARDING_STEPS.indexOf(step);
  const isLast = index === ONBOARDING_STEPS.length - 1;
  // No light dismiss: a stray press outside must not throw away setup. Escape
  // and the header's close button are kerf UI's, and the dialog `close`
  // listener in `app.tsx` maps them onto Skip — or, on the last step, onto
  // Finish (NM-245).
  return (
    <DialogSurface
      label={STEP_TITLES[step]}
      rootAttributes={{ 'data-key': 'dialog-onboarding', 'data-dialog': 'onboarding', 'data-onboarding': step }}
      size="medium"
      maxHeight="viewport"
      presentation={device.value.handset ? 'fullscreen' : 'modal'}
      footer={
        <Row gap="xs" wrap vAlign="middle">
          {/* Step markers (NM-246): a seen step is a button back to it; the
              current and later ones are disabled, so they never skip forward
              (which would bypass the profile save on the way through). Each
              names its position, so "of N" needs no hidden text. */}
          <Row gap="none" rootAttributes={{ role: 'group', 'aria-label': 'Setup Steps', 'data-onboarding-steps': '' }}>
            {ONBOARDING_STEPS.map((name, i) => (
              <Button
                icon={icon(i < index ? 'step-done' : i === index ? 'step-current' : 'step-ahead')}
                ariaLabel={`Step ${String(i + 1)} of ${String(ONBOARDING_STEPS.length)}: ${STEP_LABELS[name]}${i === index ? ' (Current)' : ''}`}
                emphasis="plain"
                size="compact"
                disabled={i >= index}
                rootAttributes={{ 'data-onboarding-step': name, 'data-current': i === index ? 'true' : undefined }}
              />
            ))}
          </Row>
          <Spacer flex />
          {/* The first step has nowhere to go back to, so it keeps the Skip
              escape; every later step — the last included — is Back
              (NM-245/246). */}
          {index === 0 ? (
            <Button emphasis="plain" action="onboarding-skip">
              Skip Setup
            </Button>
          ) : (
            <Button emphasis="plain" action="onboarding-back" icon={icon('chevron-left')}>
              Back
            </Button>
          )}
          <Button tone="brand" action="onboarding-next">
            {isLast
              ? onboardingFinishLabel(
                  // Everything Finish will add this run: ticked starters + the
                  // interests picked this run (NM-244/248), so the label can't
                  // promise "Keep current topics" over a Finish that adds topics.
                  s.onboardingTopics.length +
                    newlyPickedProfiles(s.settings.profiles, s.onboardingProfilesAtOpen).length,
                  s.onboardingExistingNames.length > 0,
                )
              : 'Continue'}
          </Button>
        </Row>
      }
    >
      <List gap="m" rootAttributes={{ 'data-dialog-body': '' }}>
        {onboardingStepJsx(step, s)}
      </List>
    </DialogSurface>
  );
}

function onboardingStepJsx(step: OnboardingStep, s: AppState): SafeHtml {
  if (step === 'welcome') {
    return (
      <List gap="xs">
        <Text>
          Name the things you want to keep up with. On a schedule you choose, Newsmonger asks an AI — with live web
          search — whether anything genuinely new has happened, and shows you only that, with links to the sources.
        </Text>
        {note(
          'Nothing is scraped or subscribed to. Each check is a fresh look, and stories you have already been shown are never repeated.',
        )}
        {note(
          'A check sends the topic’s name and focus to your AI provider and to Brave Search, plus the titles already reported for it so nothing repeats — and nothing else about you. The full note is on the Privacy page.',
        )}
      </List>
    );
  }
  if (step === 'profiles') return onboardingProfilesJsx(s);
  if (step === 'location') return onboardingLocationJsx(s);
  // Topics already watched, folded to a case-insensitive set so a starter that
  // names one is shown as watched-and-locked rather than offered again —
  // ticking it would only earn a 409 from the per-account case-insensitive name
  // uniqueness (NM-91). The names snapshot at step entry drives the running-count
  // line's "you already watch …" preface.
  const watched = new Set(s.topics.map((t) => t.name.toLowerCase()));
  return (
    <List gap="xs">
      <Text>Pick a few to start with — you can add your own, rename them, or delete them at any time.</Text>
      <ChoiceChips
        multiple="toggle"
        name="starter-topics"
        action="starter-topic"
        label="Starter Topics"
        value={STARTER_TOPICS.filter((name) => watched.has(name.toLowerCase()) || s.onboardingTopics.includes(name))}
        choices={STARTER_TOPICS.map((name) =>
          watched.has(name.toLowerCase())
            ? { value: name, label: name, disabled: true, title: 'Already watching this' }
            : { value: name, label: name },
        )}
      />
      {/* Always-present container: the suggestions block appearing must not
          restructure its siblings (see docs/3-ui.md). */}
      <div data-onboarding-suggest="">{onboardingSuggestJsx(s)}</div>
      <Text tone="quiet" size="compact" data-onboarding-count="">
        {onboardingCountText(
          s.onboardingTopics.length,
          s.topics.length - s.onboardingExistingNames.length,
          s.onboardingExistingNames,
        )}
      </Text>
    </List>
  );
}

/**
 * The profile picker (NEWS-383) — three pages of sixteen, one page at a time.
 *
 * The paging lives in this step rather than in the wizard: `Continue` advances
 * the *page* until the last one and only then advances the step, so the wizard's
 * markers keep counting steps and there is still one primary button. `app.tsx`
 * owns that branch; this file only draws the current page.
 */
function onboardingProfilesJsx(s: AppState): SafeHtml {
  const page = Math.max(0, Math.min(PROFILE_PAGE_COUNT - 1, s.onboardingProfilePage));
  const profiles = PROFILE_PAGES[page] ?? [];
  const picked = s.onboardingProfiles.length;
  return (
    <List gap="xs">
      <Text>
        Pick anything that sounds like you — as many or as few as you like. This is only used to suggest topics worth
        watching, and you can change it later in Settings.
      </Text>
      <ChoiceChips
        multiple="toggle"
        name="profiles"
        action="profile"
        label="Interests"
        value={s.onboardingProfiles.filter((id) => profiles.some((p) => p.id === id))}
        choices={profiles.map((profile) => ({ value: profile.id, label: profile.label }))}
      />
      <Row gap="xs" wrap vAlign="middle" rootAttributes={{ 'data-profile-foot': '' }}>
        <Text tone="quiet" size="compact">
          {`Page ${String(page + 1)} of ${String(PROFILE_PAGE_COUNT)}${picked === 0 ? '' : ` · ${String(picked)} chosen`}`}
        </Text>
        {/* Skips the remaining *pages*, not the whole wizard — the foot's Skip
            Setup already does that, and a user who has seen one page of these
            should be able to leave the rest without leaving setup. */}
        <Button emphasis="plain" size="compact" action="profiles-skip">
          Skip These
        </Button>
      </Row>
    </List>
  );
}

/**
 * Where the user is (NEWS-394, FR-35.9).
 *
 * Free text and nothing else — no place list, no validation, any script
 * (FR-35.2). The continent choices are six hardcoded strings that fill the same
 * field, not a picker: they exist to make "a continent is enough" believable,
 * because the hint text alone does not convince anyone.
 */
function onboardingLocationJsx(s: AppState): SafeHtml {
  const quick = LOCATION_QUICK_PICKS.find((name) => name === s.settings.location);
  return (
    <List gap="xs">
      <Text>
        Some topics are about somewhere — local events, property, schools, transport, national politics. Tell Newsmonger
        as much or as little as you like; a continent is enough, and global subjects ignore it either way.
      </Text>
      <List controlInsets="rl">
        <TextField
          name="onboarding-location"
          label="Your Location"
          value={s.settings.location}
          placeholder="e.g. Lisbon · Portugal · 東京 · the Scottish Borders"
          spellcheck={false}
          autocorrect="off"
        />
      </List>
      <ChoiceChips
        name="location-pick"
        action="location-pick"
        label="Or Pick a Continent"
        value={quick}
        choices={LOCATION_QUICK_PICKS.map((name) => ({ value: name, label: name }))}
      />
      {note(
        'Leaving this empty keeps every topic global. It is sent to your AI provider only with checks for topics that are about somewhere.',
      )}
    </List>
  );
}

/**
 * Discovery inside onboarding's Topics step (NEWS-128, then NEWS-146; FR-24.18).
 *
 * Setup is where the need is sharpest, and a brand-new user has no topics yet —
 * which makes this the one place suggestions are guaranteed unfiltered by the
 * FR-24.11 exclusions.
 *
 * This used to be a **second, smaller discovery**: a free-text box whose results
 * were chips. It answered the same question as the real dialog with a fraction
 * of its answer. So this is now a door to the real thing (NEWS-146), and it
 * opens with `data-action=open-discover` — the *same* attribute the sidebar's
 * button uses, so there is still exactly one delegate for "open discovery".
 */
function onboardingSuggestJsx(s: AppState): SafeHtml {
  // Provider configuration is environment-only (NM-12): whether a request would
  // resolve a usable provider is the server's answer, carried on every state
  // poll as `source.available`. When it can't run a check, this degrades to the
  // static starters above rather than offering a Discover button that can only
  // fail.
  if (!s.source.available) {
    return note(
      'Set an AI provider’s API key in the environment and Newsmonger can suggest topics for you — or just pick from the list.',
    );
  }
  return (
    <List gap="xs">
      <Row controlInsets="rl">
        <Button emphasis="secondary" action="open-discover" icon={icon('grid')}>
          Discover Topics
        </Button>
      </Row>
      {note('Describe what you’re into, or browse by section. Anything you add there is created straight away.')}
    </List>
  );
}
